import React, { useCallback, useEffect, useRef, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import PaintingCanvas from '../components/ui/PaintingCanvas';
import { MEMBERSHIP_PLANS, SCENE_CATALOG } from '../config/membership';
import {
  billingEnabled,
  getMembership,
  openCustomerPortal,
  startCheckout,
} from '../services/billingService';

const FAQS = [
  {
    question: 'What stays free?',
    answer: 'Focus sessions, all five Beyond the Garden artworks, planning, habits, journal, saved paintings, standard export, and backups remain free.',
  },
  {
    question: 'What happens to my artwork if I cancel?',
    answer: 'Your saved artwork remains yours. Plus tools stop renewing when the paid period ends; cancellation does not delete your paintings.',
  },
  {
    question: 'How does annual billing work?',
    answer: 'Annual membership is charged once at $49 USD for the full year. That is approximately $4.08 per month and 42% less than 12 monthly payments.',
  },
  {
    question: 'Can I purchase Studio Plus now?',
    answer: billingEnabled
      ? 'Availability is confirmed when you choose a plan. Payment happens on the hosted checkout page.'
      : 'Not yet. These are draft launch prices and purchasing has not been opened.',
  },
];

function PreviewDialog({ cadence, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const price = MEMBERSHIP_PLANS.plus.prices[cadence];

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const controls = dialogRef.current?.querySelectorAll(
        'button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!controls?.length) return;

      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-pine/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plus-dialog-title"
        aria-describedby="plus-dialog-description"
        className="w-full max-w-md rounded-3xl border border-pine/10 bg-paper p-6 shadow-2xl"
      >
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-pine/55">
          Launch offer preview
        </p>
        <h2
          id="plus-dialog-title"
          className="mt-2 font-cormorant text-3xl font-bold text-pine"
        >
          Studio Plus purchases aren't open yet.
        </h2>
        <p id="plus-dialog-description" className="mt-3 text-charcoal/70">
          Your free studio is ready to use.
        </p>
        <div className="my-5 rounded-2xl bg-sage/15 p-4 text-sm text-pine">
          Selected draft price: <strong>{price.label} USD</strong>
          {cadence === 'annual' && (
            <span className="mt-1 block">Charged as one $49 annual payment.</span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-pine/10 bg-white px-6 py-3 font-bold text-pine transition-colors hover:bg-pine/5 focus:outline-none focus:ring-2 focus:ring-pine"
          >
            Close
          </button>
          <Button as="a" href="/studio">
            Continue free
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, cadence, featured, loading, onAction }) {
  const isFree = plan.id === 'free';
  const price = isFree ? null : plan.prices[cadence];

  return (
    <Card
      variant={featured ? 'pine' : 'default'}
      className="flex h-full flex-col p-6 sm:p-8"
    >
      {featured && (
        <span className="mb-5 w-fit rounded-full bg-paper/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-paper">
          More ways to keep your work
        </span>
      )}
      <h2 className={`font-cormorant text-3xl font-bold ${featured ? 'text-paper' : 'text-pine'}`}>
        {plan.name}
      </h2>
      <div className="mt-3 min-h-16">
        {isFree ? (
          <p className="font-cormorant text-4xl font-bold text-pine">$0</p>
        ) : (
          <>
            <p className="font-cormorant text-4xl font-bold text-paper">
              ${price.amount}
              <span className="font-dm-sans text-base font-normal text-paper/70">
                /{price.cadence}
              </span>
            </p>
            {cadence === 'annual' && (
              <p className="mt-1 text-sm text-paper/75">
                {price.equivalent}. Charged $49 USD annually.
              </p>
            )}
          </>
        )}
      </div>
      <ul className={`my-6 flex-1 space-y-3 text-sm ${featured ? 'text-paper/85' : 'text-charcoal/75'}`}>
        {plan.capabilities.map((capability) => (
          <li key={capability} className="flex gap-3">
            <span aria-hidden="true" className={featured ? 'text-apricot' : 'text-sage'}>
              ●
            </span>
            <span>{capability}</span>
          </li>
        ))}
      </ul>
      {isFree ? (
        <Button as="a" href="/studio" variant="secondary" className="w-full">
          Open free studio
        </Button>
      ) : (
        <Button
          variant="paper"
          className="w-full"
          loading={loading}
          onClick={onAction}
        >
          {cadence === 'annual'
            ? 'Choose annual — $49/year'
            : 'Choose monthly — $7/month'}
        </Button>
      )}
    </Card>
  );
}

export default function Marketplace() {
  const [cadence, setCadence] = useState('annual');
  const [membership, setMembership] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let active = true;
    getMembership().then((response) => {
      if (active && response.ok && response.membership?.verified) {
        setMembership(response.membership);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const closePreview = useCallback(() => setShowPreview(false), []);
  const verifiedPlus = membership?.verified === true && membership.plan === 'plus';

  const handleUpgrade = async () => {
    if (!billingEnabled) {
      setShowPreview(true);
      return;
    }

    setLoading(true);
    setStatusMessage('');

    const response = membership?.plan === 'plus'
      ? await openCustomerPortal()
      : await startCheckout(cadence);

    setLoading(false);

    if (!response.ok) {
      if (response.status === 'unavailable') {
        setShowPreview(true);
      } else {
        setStatusMessage(response.message);
      }
      return;
    }

    window.location.assign(response.checkoutUrl || response.portalUrl);
  };

  const sampleScenes = SCENE_CATALOG.slice(0, 3);

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-10">
      <header className="grid gap-5 rounded-3xl border border-pine/10 bg-gradient-to-br from-sage/20 via-paper to-apricot/20 p-6 shadow-sm sm:p-9 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-pine/60">
            MindGarden
          </p>
          <h1 className="mt-2 max-w-2xl font-cormorant text-4xl font-bold leading-tight text-pine sm:text-5xl">
            Your effort, made visible.
          </h1>
          <p className="mt-3 max-w-2xl text-charcoal/70">
            Plan meaningful work, paint while you focus, and keep the artwork
            that your attention creates.
          </p>
        </div>
        <div className="rounded-2xl border border-pine/10 bg-white/70 px-4 py-3 text-sm text-pine">
          <span className="block text-xs uppercase tracking-wider text-pine/50">
            Current plan
          </span>
          <strong>
            {membership?.verified
              ? MEMBERSHIP_PLANS[membership.plan].name
              : 'Free access'}
          </strong>
          {!billingEnabled && (
            <span className="mt-1 block text-xs font-normal text-pine/60">
              Purchases open after billing setup.
            </span>
          )}
        </div>
      </header>

      <section aria-labelledby="pricing-title">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 id="pricing-title" className="font-cormorant text-3xl font-bold text-pine">
              A simple studio membership
            </h2>
            <p className="mt-1 text-sm text-charcoal/65">
              Free includes five Beyond the Garden artworks. Plus adds collections and high-resolution export.
            </p>
          </div>
          <div
            className="inline-flex w-fit rounded-2xl border border-pine/10 bg-white p-1"
            role="group"
            aria-label="Billing cadence"
          >
            {['monthly', 'annual'].map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={cadence === option}
                onClick={() => setCadence(option)}
                className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-pine ${
                  cadence === option
                    ? 'bg-pine text-paper'
                    : 'text-pine hover:bg-sage/15'
                }`}
              >
                {option === 'monthly' ? 'Monthly' : 'Annual · save 42%'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <PlanCard plan={MEMBERSHIP_PLANS.free} cadence={cadence} />
          <PlanCard
            plan={MEMBERSHIP_PLANS.plus}
            cadence={cadence}
            featured
            loading={loading}
            onAction={handleUpgrade}
          />
        </div>

        {!billingEnabled && (
          <p className="mt-4 rounded-2xl border border-pine/10 bg-white p-4 text-sm text-pine/70">
            Explore the Plus experience. Purchases aren’t open yet.
            {' '}
            <Button as="a" href="/studio" variant="ghost" size="sm">
              Try scene previews
            </Button>
          </p>
        )}

        {statusMessage && (
          <p
            role="alert"
            className="mt-3 rounded-2xl bg-crimson/10 p-3 text-sm text-crimson"
          >
            {statusMessage}
          </p>
        )}
      </section>

      <Card className="p-6 sm:p-8">
        <div className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-pine/50">
              Beyond the Garden preview
            </p>
            <h2 className="mt-2 font-cormorant text-3xl font-bold text-pine">
              Five free painted artworks
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
              Preview the free artworks in Studio. Collections and high-resolution export are included with Studio Plus when memberships open.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {sampleScenes.map((scene, index) => (
              <div
                key={scene.id}
                className={`aspect-square rounded-2xl border border-pine/10 overflow-hidden relative ${
                  index === 0
                    ? 'bg-gradient-to-br from-apricot/70 to-goldenrod/30'
                    : index === 1
                      ? 'bg-gradient-to-br from-skyblue/40 to-sage/50'
                      : 'bg-gradient-to-br from-lavender/60 to-apricot/35'
                }`}
              >
                <PaintingCanvas
                  id={`membership-scene-${scene.id}`}
                  scene={scene.id}
                  progress={1}
                  artSeed={7300 + index}
                  className="absolute inset-0 w-full h-full object-contain"
                />
                <span className="absolute left-3 top-3 rounded-full bg-paper/80 px-2 py-1 text-xs font-bold text-pine">
                  Free sample · {scene.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <section aria-labelledby="faq-title">
        <h2 id="faq-title" className="mb-4 font-cormorant text-3xl font-bold text-pine">
          Membership questions
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          {FAQS.map((item) => (
            <details
              key={item.question}
              className="rounded-2xl border border-pine/10 bg-white p-5 text-charcoal shadow-sm"
            >
              <summary className="cursor-pointer font-bold text-pine focus:outline-none focus-visible:ring-2 focus-visible:ring-pine">
                {item.question}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </section>

      <p className="text-center text-xs text-charcoal/55">
        Legacy purchases and existing garden benefits remain unchanged.
      </p>

      {showPreview && (
        <PreviewDialog cadence={cadence} onClose={closePreview} />
      )}
    </div>
  );
}